import { useCallback, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import {
  CardSelectableContainer,
  CardSelectableItem,
  Input,
  Textarea,
} from '@factorialco/f0-react/dist/experimental'
import { BankAccountSelectorF0Query } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useAddPaymentRequestsForm } from 'modules/banking/components/AddPaymentRequestsToPaymentForm/useForm'
import { BankAccountSelectorF0 } from 'modules/banking/components/BankAccountSelectorF0'
import { BulkPaymentSelector } from 'modules/banking/components/BulkPaymentSelector'
import { useCreatePaymentFromPaymentRequestsForm } from 'modules/banking/components/CreateBulkPaymentForm/useFormFromPaymentRequests'
import { supportsSepaPayment } from 'modules/banking/lib/paymentHelpers'

type BankAccountOption = NonNullable<
  BankAccountSelectorF0Query['banking']
>['bankAccountsConnection']['edges'][number]['node']

// Minimal type for payment request data needed by the modal
export interface PaymentRequestForBulkPayment {
  id: number
  amountCents: number
  currency: string
  dueDate?: string | null
}

export interface PayPaymentRequestsInBulkModalProps {
  onClose: () => void
  onSuccess: (id?: number) => void
  paymentRequests: PaymentRequestForBulkPayment[]
  enablePaymentMethodFilter?: boolean
}

type FormType = 'new' | 'existing'
type Step = 1 | 2
type PaymentMethod = 'sepa' | 'transfer'

export const PayPaymentRequestsInBulkModal = ({
  onClose,
  onSuccess,
  paymentRequests,
  enablePaymentMethodFilter = false,
}: PayPaymentRequestsInBulkModalProps) => {
  const [step, setStep] = useState<Step>(1)
  const [formType, setFormType] = useState<FormType | undefined>(undefined)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('transfer')
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountOption | null>(null)
  const [selectedPaymentId, setSelectedPaymentId] = useState<number>(-1)

  const paymentRequestIds = useUnsafeMemo(
    () => paymentRequests.map((pr) => pr.id),
    [paymentRequests]
  )

  const { form: creationForm, loading: loadingCreation } = useCreatePaymentFromPaymentRequestsForm({
    onSuccess: onSuccess,
    paymentRequests,
  })

  const handleAddToPaymentSuccess = useCallback(() => {
    onSuccess(selectedPaymentId)
  }, [onSuccess, selectedPaymentId])

  const { form: addToPaymentForm, loading: loadingAddition } = useAddPaymentRequestsForm({
    onSuccess: handleAddToPaymentSuccess,
    paymentRequestIds,
  })

  const cardSelectableItems = useUnsafeMemo<CardSelectableItem<FormType>[]>(
    () => [
      {
        value: 'new',
        title: i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.new.title'),
        description: i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.new.description'),
      },
      {
        value: 'existing',
        title: i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.existing.title'),
        description: i18n.t(
          'banking.paymentRequests.pay_in_bulk_modal.options.existing.description'
        ),
      },
    ],
    []
  )

  const isStep1Valid = formType !== undefined
  const isStep2Valid =
    formType === 'new'
      ? !!creationForm.values.bankAccountId && !!creationForm.values.label
      : formType === 'existing'
        ? addToPaymentForm.values.paymentId !== -1
        : false

  const handleNext = () => {
    if (step === 1 && isStep1Valid) {
      setStep(2)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
    }
  }

  const handleSubmit = async () => {
    track(trackingEvents.accounting.payments.clickSavePayInBulk, {
      bulk_payment_type: formType === 'new' ? 'new_bulk_payment' : 'add_to_existing_payment',
      source: 'payment_requests',
    })

    if (formType === 'new') {
      await creationForm.submitForm()
    } else if (formType === 'existing') {
      await addToPaymentForm.submitForm()
    }
  }

  const handleSelectBankAccount = (bankAccount: BankAccountOption | null) => {
    creationForm.setFieldValue('bankAccountId', bankAccount?.id)
    setSelectedBankAccount(bankAccount)
  }

  const handlePaymentMethodChange = (value: PaymentMethod | undefined) => {
    if (!value) return
    setPaymentMethod(value)
    setSelectedBankAccount(null)
    creationForm.setFieldValue('bankAccountId', '')
  }

  const paymentMethodOptions: F0SelectItemObject<PaymentMethod>[] = [
    {
      value: 'sepa',
      label: i18n.t('banking.bulk_payment.forms.fields.payment_method.options.sepa'),
    },
    {
      value: 'transfer',
      label: i18n.t('banking.bulk_payment.forms.fields.payment_method.options.transfer'),
    },
  ]

  const bankAccountFilter = useUnsafeMemo(
    () =>
      enablePaymentMethodFilter
        ? (account: BankAccountOption) => {
            const services = account.account?.providerConfiguration?.services ?? []
            return paymentMethod === 'sepa'
              ? !supportsSepaPayment(services)
              : supportsSepaPayment(services)
          }
        : undefined,
    [enablePaymentMethodFilter, paymentMethod]
  )

  const isLoading = loadingCreation || loadingAddition

  const getStepTitle = () => {
    return i18n.t('banking.paymentRequests.pay_in_bulk_modal.title')
  }

  const getStepDescription = () => {
    if (step === 1) {
      return undefined
    }
    return formType === 'new'
      ? i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.new.title')
      : i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.existing.title')
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (isLoading) return
        onClose()
      }}
      title={getStepTitle()}
      description={getStepDescription()}
      primaryAction={
        step === 1
          ? {
              label: i18n.t('common.continue'),
              onClick: handleNext,
              disabled: !isStep1Valid,
            }
          : {
              label: i18n.t('common.continue'),
              onClick: handleSubmit,
              disabled: !isStep2Valid || isLoading,
              loading: isLoading,
            }
      }
      secondaryAction={
        step === 1
          ? {
              label: i18n.t('banking.paymentRequests.pay_in_bulk_modal.actions.cancel'),
              onClick: onClose,
            }
          : {
              label: i18n.t('common.back'),
              onClick: handleBack,
            }
      }
    >
      {step === 1 && (
        <CardSelectableContainer
          items={cardSelectableItems}
          value={formType}
          onChange={setFormType}
          label={i18n.t('banking.paymentRequests.pay_in_bulk_modal.description')}
        />
      )}

      {step === 2 && formType === 'new' && (
        <Box gap="s16" flexDirection="column">
          <Input
            required
            label={i18n.t('banking.bulk_payment.forms.fields.name.label')}
            placeholder={i18n.t('banking.bulk_payment.forms.fields.name.placeholder')}
            value={creationForm.values.label}
            onChange={(value) => creationForm.setFieldValue('label', value)}
            error={creationForm.errors.label}
          />
          {enablePaymentMethodFilter && (
            <F0Select<PaymentMethod>
              label={i18n.t('banking.bulk_payment.forms.fields.payment_method.label')}
              placeholder={i18n.t('banking.bulk_payment.forms.fields.payment_method.placeholder')}
              options={paymentMethodOptions}
              value={paymentMethod}
              onChange={(value: PaymentMethod) => handlePaymentMethodChange(value)}
              required
            />
          )}
          <BankAccountSelectorF0
            key={paymentMethod}
            value={selectedBankAccount}
            onChange={handleSelectBankAccount}
            accountNumberTypes={['iban']}
            providerService="sepa_payment_management"
            currency="EUR"
            required={!selectedBankAccount}
            autoSelectSingle
            filter={bankAccountFilter}
          />
          <Textarea
            label={i18n.t('banking.bulk_payment.forms.fields.opt_description.label')}
            placeholder={i18n.t('banking.bulk_payment.forms.fields.opt_description.placeholder')}
            value={creationForm.values.description}
            onChange={(value) => creationForm.setFieldValue('description', value)}
          />
        </Box>
      )}

      {step === 2 && formType === 'existing' && (
        <Box gap="s16" flexDirection="column">
          <BulkPaymentSelector
            value={addToPaymentForm.values.paymentId}
            onChange={(paymentId) => {
              addToPaymentForm.setFieldValue('paymentId', paymentId)
              setSelectedPaymentId(paymentId)
            }}
          />
        </Box>
      )}
    </F0Dialog>
  )
}
